import {
  getPhotosOptions,
  getPhotosQueryKey,
  getRawPhotosOptions,
  getRawPhotosQueryKey,
  // Hey API SDK mixed up the names of the functions
  getRawPhotoQueryKey as getPhotoQueryKey,
  getRawPhotoOptions as getPhotoOptions,
  getPhotoQueryKey as getRawPhotoQueryKey,
  getPhotoOptions as getRawPhotoOptions,
  getPhotos3dOptions,
  getPhotos3dQueryKey,
  getPhotosThumbnailOptions,
  getPhotosThumbnailQueryKey,
} from '@/api/navigationApi'
import { ImageType } from '@/components/Vehicle/types'
import { useQuery } from '@tanstack/react-query'
import useBlobUrl from './useBlobUrl'
import {
  VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
  VEHICLE_IMAGE_STALE_TIME_MS,
  VEHICLE_IMAGE_THUMBNAIL_REFETCH_INTERVAL_MS,
} from '@/constants'
import { Client } from '@/api/navigationApi/client'
import { DataSyncResponse } from '@/api/navigationApi/types.gen'
import { useMemo } from 'react'
import { extendKey } from '@/utils/queryKey'

const enabled = (imageType: ImageType) => ({
  fetchThumbnail: imageType === 'thumbnail',
  fetchRaw: imageType === 'raw-photos',
  fetchPhotos: imageType === 'photos',
  fetchPhotos3d: imageType === 'photos-3d',
  fetchPhotoHqFl: imageType === 'photo-hq/FL',
  fetchPhotoHqFc: imageType === 'photo-hq/FC',
  fetchPhotoHqFr: imageType === 'photo-hq/FR',
  fetchRawPhotoHqFl: imageType === 'raw-photo-hq/FL',
  fetchRawPhotoHqFc: imageType === 'raw-photo-hq/FC',
  fetchRawPhotoHqFr: imageType === 'raw-photo-hq/FR',
})

const useGetVehicleImage = ({
  imageType,
  vehicleId,
  client,
  syncStatus,
}: {
  imageType: ImageType
  vehicleId: string
  client?: Client
  syncStatus?: DataSyncResponse['status']
}) => {
  const carNotCableConnected = useMemo(() => syncStatus !== 'InProgress' && syncStatus !== 'Finished', [syncStatus])

  const {
    data: thumbnail,
    isLoading: isLoadingThumbnail,
    isRefetching: isRefetchingThumbnail,
    status: statusThumbnail,
  } = useQuery({
    ...extendKey(getPhotosThumbnailOptions({ client }), vehicleId, imageType),
    queryKey: [...getPhotosThumbnailQueryKey({ client }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_THUMBNAIL_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchThumbnail && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: photos,
    status,
    refetch,
    isRefetching: isRefetchingPhotos,
    isLoading: isLoadingPhotos,
  } = useQuery({
    ...extendKey(getPhotosOptions({ client }), vehicleId, imageType),
    queryKey: [...getPhotosQueryKey({ client }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchPhotos && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: photosRaw,
    isLoading: isLoadingRaw,
    isRefetching: isRefetchingRaw,
    status: statusRaw,
  } = useQuery({
    ...extendKey(getRawPhotosOptions({ client }), vehicleId, imageType),
    queryKey: [...getRawPhotosQueryKey({ client }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchRaw && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: photos3d,
    isLoading: isLoading3d,
    isRefetching: isRefetching3d,
    status: status3d,
  } = useQuery({
    ...extendKey(getPhotos3dOptions({ client }), vehicleId, imageType),
    queryKey: [...getPhotos3dQueryKey({ client }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchPhotos3d && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: photoHqFl,
    isLoading: isLoadingPhotoHqFl,
    isRefetching: isRefetchingPhotoHqFl,
    status: statusPhotoHqFl,
  } = useQuery({
    ...extendKey(getPhotoOptions({ client, path: { camera: 'FL' } }), vehicleId, imageType),
    queryKey: [...getPhotoQueryKey({ client, path: { camera: 'FL' } }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchPhotoHqFl && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: photoHqFc,
    isLoading: isLoadingPhotoHqFc,
    isRefetching: isRefetchingPhotoHqFc,
    status: statusPhotoHqFc,
  } = useQuery({
    ...extendKey(getPhotoOptions({ client, path: { camera: 'FC' } }), vehicleId, imageType),
    queryKey: [...getPhotoQueryKey({ client, path: { camera: 'FC' } }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchPhotoHqFc && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: photoHqFr,
    isLoading: isLoadingPhotoHqFr,
    isRefetching: isRefetchingPhotoHqFr,
    status: statusPhotoHqFr,
  } = useQuery({
    ...extendKey(getPhotoOptions({ client, path: { camera: 'FR' } }), vehicleId, imageType),
    queryKey: [...getPhotoQueryKey({ client, path: { camera: 'FR' } }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchPhotoHqFr && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: rawPhotoHqFl,
    isLoading: isLoadingRawPhotoHqFl,
    isRefetching: isRefetchingRawPhotoHqFl,
    status: statusRawPhotoHqFl,
  } = useQuery({
    ...extendKey(getRawPhotoOptions({ client, path: { camera: 'FL' } }), vehicleId, imageType),
    queryKey: [...getRawPhotoQueryKey({ client, path: { camera: 'FL' } }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchRawPhotoHqFl && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: rawPhotoHqFc,
    isLoading: isLoadingRawPhotoHqFc,
    isRefetching: isRefetchingRawPhotoHqFc,
    status: statusRawPhotoHqFc,
  } = useQuery({
    ...extendKey(getRawPhotoOptions({ client, path: { camera: 'FC' } }), vehicleId, imageType),
    queryKey: [...getRawPhotoQueryKey({ client, path: { camera: 'FC' } }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchRawPhotoHqFc && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const {
    data: rawPhotoHqFr,
    isLoading: isLoadingRawPhotoHqFr,
    isRefetching: isRefetchingRawPhotoHqFr,
    status: statusRawPhotoHqFr,
  } = useQuery({
    ...extendKey(getRawPhotoOptions({ client, path: { camera: 'FR' } }), vehicleId, imageType),
    queryKey: [...getRawPhotoQueryKey({ client, path: { camera: 'FR' } }), vehicleId, imageType],
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: enabled(imageType).fetchRawPhotoHqFr && carNotCableConnected,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const isLoading =
    isLoadingThumbnail ||
    isLoadingPhotos ||
    isLoadingRaw ||
    isLoading3d ||
    isLoadingPhotoHqFl ||
    isLoadingPhotoHqFc ||
    isLoadingPhotoHqFr ||
    isLoadingRawPhotoHqFl ||
    isLoadingRawPhotoHqFc ||
    isLoadingRawPhotoHqFr

  const error =
    statusThumbnail === 'error' ||
    status === 'error' ||
    statusRaw === 'error' ||
    status3d === 'error' ||
    statusPhotoHqFl === 'error' ||
    statusPhotoHqFc === 'error' ||
    statusPhotoHqFr === 'error' ||
    statusRawPhotoHqFl === 'error' ||
    statusRawPhotoHqFc === 'error' ||
    statusRawPhotoHqFr === 'error'

  const data =
    thumbnail ||
    photos ||
    photosRaw ||
    photos3d ||
    photoHqFl ||
    photoHqFc ||
    photoHqFr ||
    rawPhotoHqFl ||
    rawPhotoHqFc ||
    rawPhotoHqFr

  const isRefetching =
    isRefetchingThumbnail ||
    isRefetchingPhotos ||
    isRefetchingRaw ||
    isRefetching3d ||
    isRefetchingPhotoHqFl ||
    isRefetchingPhotoHqFc ||
    isRefetchingPhotoHqFr ||
    isRefetchingRawPhotoHqFl ||
    isRefetchingRawPhotoHqFc ||
    isRefetchingRawPhotoHqFr

  const url = useBlobUrl(data as Blob | undefined)

  return {
    data,
    url,
    isLoading,
    error,
    refetch,
    isRefetching,
    currentImageType: imageType,
  }
}

export default useGetVehicleImage
